import {
  type PayloadAction,
  createAsyncThunk,
  createSlice
} from '@reduxjs/toolkit';

import {
  type IGetPaymentScheduleParams,
  contractsApi
} from '@/api/contracts.api';
import type {
  TContractDetails,
  TPaymentSchedule
} from '@/pages/ContractInfo/types/contract.types';
import { keysToCamelCase } from '@/shared/services/tools';
import type { TLeasingFile } from '@/shared/types/files.types';

export const getContractInfo = createAsyncThunk(
  'contractInfo/getContractInfo',
  (id: string) => {
    return contractsApi.getContractInfo(id);
  }
);

export const getPaymentSchedule = createAsyncThunk(
  'contractInfo/getPaymentSchedule',
  (params: IGetPaymentScheduleParams) => {
    return contractsApi.getPaymentSchedule(params);
  }
);

interface IContractInfoState {
  contractInfo: TContractDetails | null;
  paymentInfo: TPaymentSchedule;
  isEditing: boolean;
}

const initialState: IContractInfoState = {
  contractInfo: null,
  paymentInfo: {
    data: [],
    stats: {
      balance: 0,
      overdue: 0,
      paid: 0,
      pending: 0,
      total: 0,
      outstandingPrincipal: 0,
      paidPrincipal: 0,
      paidInterest: 0,
      totalOverpayment: 0,
      overpaymentDifference: 0
    }
  },
  isEditing: false
};

const contractInfoSlice = createSlice({
  name: 'contractInfo',
  initialState,
  reducers: {
    setIsEditing: (state, action: PayloadAction<boolean>) => {
      state.isEditing = action.payload;
    }
  },
  extraReducers: (builder) => {
    builder
      .addCase(getContractInfo.fulfilled, (state, action) => {
        const { request, ...rest } = action.payload;
        const { Files, ...requestRest } = request;
        const camelCaseContractInfo = {
          ...keysToCamelCase(rest),
          request: {
            ...keysToCamelCase(requestRest),
            files: Files?.reduce(
              (acc, file) => {
                if (file?.file_type?.code) {
                  acc[file.file_type.code] = keysToCamelCase(file);
                }
                return acc;
              },
              {} as Record<string, TLeasingFile>
            )
          }
        };
        state.contractInfo = camelCaseContractInfo;
      })
      .addCase(getContractInfo.rejected, (state) => {
        state.contractInfo = null;
      })
      .addCase(getPaymentSchedule.fulfilled, (state, action) => {
        state.paymentInfo = keysToCamelCase(action.payload);
      })
      .addCase(getPaymentSchedule.rejected, (state) => {
        state.paymentInfo = {
          data: [],
          stats: { balance: 0, overdue: 0, paid: 0, pending: 0, total: 0 }
        };
      });
  }
});

export const { setIsEditing } = contractInfoSlice.actions;

export default contractInfoSlice;
